import { FC, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import type { TemplateSignatoryRole } from '../../../constants/template-signatory-type.constant';
import type { TemplateByIdResponse } from '../../../hooks/use-template-by-id.hook';

import { CrossIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { ModalWrapper } from '../../../../../../components/modal-wrapper/index';
import { useLocale } from '../../../../../../hooks/index';
import { templateSignatoryTypeColor } from '../../../constants/stakeholder-type-color.constant';
import {
  getTemplateSignatoryBaseDisplayLabel,
  getTemplateSignatoryBaseType,
} from '../../../constants/template-signatory-type.constant';
import { SIGNATURE_FIELD_TYPE_LABEL } from '../../../enums/signature-field-type-label.const';
import { useTemplateDocument } from '../../../hooks/use-template-document.hook';
import { MappingEditorStore } from '../../../store/mapping-editor.store';
import { getFieldMappingDisplayLabel } from '../../../utils/field-mapping-display-label.util';
import { DocumentPreview } from './document/document-preview';

export type ReviewTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  template: TemplateByIdResponse;
};

export const ReviewTemplateModal: FC<ReviewTemplateModalProps> = ({
  isOpen,
  onClose,
  template,
}) => {
  const { file, isError: fileError } = useTemplateDocument({
    templateId: template?.id ?? null,
    enabled: isOpen,
    fileLink: template.fileLink,
  });

  const setVariableMappings = MappingEditorStore(({ setVariableMappings }) => setVariableMappings);

  const { messagesLocale, messagesLocaleFn } = useLocale();

  const signatories = Array.from(
    new Set(
      (template.signatoryTypes ?? []).map<TemplateSignatoryRole>((s) =>
        getTemplateSignatoryBaseType(s.stakeholderType),
      ),
    ),
  );
  const fieldMappings = template.fieldMapping
    ? Object.entries(template.fieldMapping).map(([placeholder, field]) => ({ field, placeholder }))
    : [];
  const signatureFields = template.tabs ?? [];

  useEffect(() => {
    if (!isOpen || !template) return;

    setVariableMappings(template.fieldMapping ?? {});
  }, [isOpen, template, setVariableMappings]);

  return (
    <ModalWrapper
      className={twMerge(
        '!bottom-[25px] !left-[66px] !right-[66px] !top-[25px] !z-[61] flex !h-auto !w-auto !max-w-none !translate-x-0 !translate-y-0 flex-col overflow-hidden rounded-xl bg-white shadow-lg',
      )}
      control={{
        open: isOpen,
        onOpenChange: (open: boolean) => {
          if (!open) onClose();
        },
      }}
      onBgClick={onClose}
      overlayClassName="z-[60]"
      side="CENTER"
    >
      <div className={twMerge('flex min-h-0 flex-1 flex-col', 'overflow-hidden')}>
        <div className="mt-6 flex h-8 shrink-0 items-center justify-between px-6">
          <h2 className="text-[20px] font-semibold text-gray-700">
            <AppFormattedMessage id="REVIEW_TEMPLATE" />
          </h2>
          <Button
            className="h-8 w-8 rounded-full border border-[#F2F2F2] p-0 shadow-[0_0_18.667px_0_rgba(0,0,0,0.08)]"
            onClick={onClose}
            styleType="NONE"
            type="button"
          >
            <CrossIcon className="h-4 w-4" iconColor="#344054" />
          </Button>
        </div>

        <div className="mt-4 flex shrink-0 items-center gap-2 px-6">
          {signatories.map((signatory) => (
            <span
              className="rounded-[16px] px-2.5 py-1 text-xs leading-[18px]"
              key={signatory}
              style={{
                color: templateSignatoryTypeColor[getTemplateSignatoryBaseType(signatory)],
                backgroundColor: `${templateSignatoryTypeColor[getTemplateSignatoryBaseType(signatory)]}1A`,
              }}
            >
              {getTemplateSignatoryBaseDisplayLabel(signatory, messagesLocale)}
            </span>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 gap-6 overflow-hidden px-6 py-6">
          {signatureFields.length > 0 && (
            <aside
              className="flex w-80 shrink-0 flex-col overflow-hidden rounded-2xl bg-white"
              style={{
                boxShadow:
                  '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
              }}
            >
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <span className="text-xs font-[450] text-gray-500">
                  <AppFormattedMessage id="SIGNATURE_FIELDS" />
                </span>
                <span className="text-xs font-medium text-[#7F56D9]">
                  {signatureFields.length}/{signatureFields.length}
                </span>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-4">
                {signatureFields.map((tab, index) => (
                  <div
                    className="rounded border border-gray-100 p-2 text-xs text-gray-700"
                    key={`${tab.anchorString}-${index}`}
                  >
                    <p className="mb-3 break-all font-medium text-[#7F56D9]">{tab.anchorString}</p>
                    <p className="mb-1">
                      <span className="mb-1 text-gray-500">Type: </span>
                      {tab.type != null
                        ? messagesLocaleFn(SIGNATURE_FIELD_TYPE_LABEL[tab.type])
                        : '—'}
                    </p>
                    <p className="mb-1">
                      <span className="text-gray-500">Stakeholder: </span>
                      {tab.stakeholderType != null
                        ? getTemplateSignatoryBaseDisplayLabel(tab.stakeholderType, messagesLocale)
                        : '—'}
                    </p>
                    <p className="mb-1">
                      <span className="text-gray-500">Signer: </span>
                      {tab.signerStakeholder?.fullName ?? '—'}
                    </p>
                  </div>
                ))}
              </div>
            </aside>
          )}

          <DocumentPreview file={file ?? undefined} fileName={template.documentName ?? undefined} />

          <aside
            className="flex w-80 shrink-0 flex-col overflow-hidden rounded-2xl bg-white"
            style={{
              boxShadow:
                '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
            }}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <span className="text-xs font-[450] text-gray-500">
                <AppFormattedMessage id="VARIABLE_MAPPINGS" />
              </span>
              <span className="text-xs font-medium text-forest-600">
                {fieldMappings.length}/{fieldMappings.length}
              </span>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-4">
              {fileError && (
                <p className="text-xs text-fireside-600">
                  <AppFormattedMessage id="UNEXPECTED_ERROR_OCCURRED" />
                </p>
              )}
              {fieldMappings.map((mapping) => (
                <div
                  className="flex flex-col gap-2 rounded-lg border border-gray-100 p-3"
                  key={mapping.placeholder}
                >
                  <div className="break-all text-xs font-[450] text-forest-600">
                    {`{{${mapping.placeholder}}}`}
                  </div>
                  <div className="bg-gray-50 px-3 py-2 text-xs font-[450] text-gray-700 [overflow-wrap:anywhere]">
                    {getFieldMappingDisplayLabel({
                      messagesLocale,
                      messagesLocaleFn,
                      storedValue: mapping.field,
                    })}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>

        <div className="flex shrink-0 justify-end bg-gray-100 px-6 py-4">
          <Button
            className="bg-brand-700 px-4 py-2 text-sm font-medium text-white"
            onClick={onClose}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="CLOSE" />
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
